import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import LoginPage from "./components/LoginPage";
import Dashboard from "./components/DashBoard";
import UserDetail from "./components/UserDetail";
import AddCustomer from "./components/AddCustomer";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import AddAdmin from "./components/AddAdmin";
import AdminList from "./components/AdminList";

// Context for managing authentication state across the app
export const AuthContext = createContext();

// Function to validate the token by sending it to the backend
const validateToken = async (token) => {
  try {
    const response = await fetch("/api/verify-token", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.ok;
  } catch {
    return false; // Fallback for cases where the request fails
  }
};

// Component to handle rendering errors
const ErrorBoundary = ({ children }) => {
  const [hasError] = useState(false);

  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full px-6 py-8 bg-white shadow-lg rounded-lg">
          <h1 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h1>
          <p className="text-gray-600">Please try refreshing the page</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// Spinner component displayed during loading states
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

// Authentication provider to manage user authentication and loading state
const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Function to validate authentication on initial load
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        const isValid = await validateToken(token);
        setIsAuthenticated(isValid);
        if (!isValid) {
          localStorage.removeItem("token"); // Clear invalid token
        }
      }
      setLoading(false); // Stop loading once validation completes
    };

    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, setIsAuthenticated }}>
      {loading ? <LoadingSpinner /> : children}
    </AuthContext.Provider>
  );
};

// Component to protect routes, ensuring only authenticated users can access them
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const token = localStorage.getItem("token");

  if (!token || !isAuthenticated) {
    return <Navigate to="/" replace />; // Redirect to login if not authenticated
  }
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route
              path="/"
              element={
                <AuthContext.Consumer>
                  {({ isAuthenticated, setIsAuthenticated }) =>
                    isAuthenticated ? (
                      <Navigate to="/dashboard" replace /> // Redirect if authenticated
                    ) : (
                      <LoginPage setIsAuthenticated={setIsAuthenticated} />
                    )
                  }
                </AuthContext.Consumer>
              }
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AuthContext.Consumer>
                    {({ setIsAuthenticated }) => (
                      <Dashboard setIsAuthenticated={setIsAuthenticated} />
                    )}
                  </AuthContext.Consumer>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/:userID"
              element={
                <ProtectedRoute>
                  <AuthContext.Consumer>
                    {({ setIsAuthenticated }) => (
                      <UserDetail setIsAuthenticated={setIsAuthenticated} />
                    )}
                  </AuthContext.Consumer>
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-customer"
              element={
                <ProtectedRoute>
                  <AddCustomer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-list"
              element={
                <ProtectedRoute>
                  <AdminList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-admin"
              element={
                <ProtectedRoute>
                  <AddAdmin />
                </ProtectedRoute>
              }
            />

            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;